/**
 * Centralized Chart Layout Constants for PowerPoint Export
 * Ensures consistent positioning, sizing, and formatting across all chart exports
 */

/**
 * Standard content starting position
 * This matches the Y position of profile avatar/name for consistency
 */
export const CONTENT_START_Y = 1.2;

/**
 * Standard chart positions and sizes
 * All measurements in PowerPoint inches
 */
export const CHART_LAYOUTS = {
  // Full-width chart (centered, most common layout)
  FULL_WIDTH: {
    x: 0.5,
    y: CONTENT_START_Y,
    w: 12.3,
    h: 5.8,
  },

  // Half-width charts (for side-by-side layouts)
  LEFT_HALF: {
    x: 0.5,
    y: CONTENT_START_Y,
    w: 6.0,
    h: 5.8,
  },

  RIGHT_HALF: {
    x: 6.8,
    y: CONTENT_START_Y,
    w: 6.0,
    h: 5.8,
  },

  // Profile overview specific layouts
  PROFILE_LEFT_CHART: {
    x: 0.5,
    y: 3.45,
    w: 6.0,
    h: 3.2,
  },

  PROFILE_RIGHT_CHART: {
    x: 6.8,
    y: 3.45,
    w: 6.0,
    h: 3.2,
  },

  // Wide chart (for Share of Voice and similar)
  WIDE_CENTERED: {
    x: 1.5,
    y: CONTENT_START_Y,
    w: 10,
    h: 5.8,
  },

  // Tall chart (for vertical bar charts)
  TALL_CENTERED: {
    x: 0.5,
    y: CONTENT_START_Y,
    w: 12.3,
    h: 5.8,
  },

  // Share of Voice bar chart (left side, leaves room for table)
  SOV_BAR_CHART: {
    x: 0.5,
    y: 1.8, // Below summary bar
    w: 8.0, // Leave room for table on the right
    h: 5.0,
  },

  // Share of Voice table (right side, next to chart)
  SOV_TABLE: {
    x: 8.7, // To the right of the chart
    y: 1.8, // Below summary bar
    w: 4.0, // Table width
    h: 5.0, // Table height
  },
};

/**
 * Standard chart formatting options
 * Single source of truth for all chart exports (single, profile, project)
 */
export const CHART_FORMATS = {
  // Doughnut chart standard format
  DOUGHNUT: {
    holeSize: 50,
    showLegend: true,
    legendPos: 'b', // bottom
    legendFontSize: 8, // Standardized to 8pt
    showPercent: false, // Disable percentages to avoid formatting issues
    showLabel: false, // Don't show category name (shown in legend)
    showValue: true, // Show actual value only
    dataLabelFontSize: 8, // Standardized to 8pt
    dataLabelColor: '000000', // black for readability
    dataLabelPosition: 'bestFit', // Let PowerPoint find best position
    dataLabelFormatCode: '#,##0', // Format numbers with thousands separator
    useSourceColors: true, // Use source-specific colors when available
  },

  // Pie chart standard format
  PIE: {
    holeSize: 0,
    showLegend: true,
    legendPos: 'b', // bottom
    legendFontSize: 8, // Standardized to 8pt
    showPercent: false, // Disable percentages to avoid formatting issues
    showLabel: false, // Don't show category name (shown in legend)
    showValue: true, // Show actual value only
    dataLabelFontSize: 8, // Standardized to 8pt
    dataLabelColor: '000000', // black for readability
    dataLabelPosition: 'bestFit', // Let PowerPoint find best position
    dataLabelFormatCode: '#,##0', // Format numbers with thousands separator
    useSourceColors: true, // Use source-specific colors when available
  },

  // Bar chart standard format (horizontal)
  BAR_HORIZONTAL: {
    barDir: 'bar',
    showLegend: true, // Show legend for consistency
    legendPos: 'b', // bottom
    legendFontSize: 8, // Standardized to 8pt
    dataLabelPosition: 'outEnd',
    showDataLabels: true,
    dataLabelFontSize: 8, // Standardized to 8pt
    dataLabelColor: '000000', // Black for readability
    catAxisLabelFontSize: 8, // Category axis labels 8pt
    valAxisLabelFontSize: 8, // Value axis labels 8pt
    sortDescending: true, // Always sort horizontally desc
    useSourceColors: true, // Use source-specific colors when available
  },

  // Bar chart standard format (vertical)
  BAR_VERTICAL: {
    barDir: 'col',
    showLegend: true, // Show legend for consistency
    legendPos: 'b', // bottom
    legendFontSize: 8, // Standardized to 8pt
    dataLabelPosition: 'outEnd',
    showDataLabels: true,
    dataLabelFontSize: 8, // Standardized to 8pt
    dataLabelColor: '000000', // Black for readability
    catAxisLabelFontSize: 8, // Category axis labels 8pt
    valAxisLabelFontSize: 8, // Value axis labels 8pt
    sortDescending: true, // Sort largest to smallest (left to right)
    useSourceColors: true, // Use source-specific colors when available
  },

  // Stacked bar chart format (typically for timelines)
  BAR_STACKED: {
    barDir: 'col',
    barGrouping: 'stacked',
    showLegend: true,
    legendPos: 'b', // bottom for consistency
    legendFontSize: 8, // Standardized to 8pt
    showDataLabels: false, // Too cluttered with stacked bars
    dataLabelFontSize: 8, // Standardized to 8pt
    dataLabelColor: '000000', // Black for readability
    catAxisLabelFontSize: 8, // Category axis labels 8pt
    valAxisLabelFontSize: 8, // Value axis labels 8pt
    sortDescending: false, // Maintain timeline order
    useSourceColors: false, // Stacked bars use product colors, not source colors
  },

  // Line chart standard format
  LINE: {
    showLegend: true,
    legendPos: 'b', // bottom
    legendFontSize: 8, // Standardized to 8pt
    lineDataSymbol: 'none',
    lineSize: 2,
    showDataLabels: false, // Line charts typically don't show data labels
    dataLabelFontSize: 8, // Standardized to 8pt
    catAxisLabelFontSize: 8, // Category axis labels 8pt
    valAxisLabelFontSize: 8, // Value axis labels 8pt
  },
};

/**
 * Standard color options
 */
export const COLOR_OPTIONS = {
  // Use source-specific colors (Twitter orange, Facebook blue, etc.)
  USE_SOURCE_COLORS: true,

  // Use default chart colors
  USE_DEFAULT_COLORS: false,
};

/**
 * Get standard layout for a chart type
 * @param {string} chartType - Type of chart (doughnut, pie, bar, line)
 * @param {string} position - Position variant (full, left, right, wide)
 * @returns {Object} Layout configuration
 */
export const getChartLayout = (chartType, position = 'full') => {
  switch (position) {
    case 'left':
      return CHART_LAYOUTS.LEFT_HALF;
    case 'right':
      return CHART_LAYOUTS.RIGHT_HALF;
    case 'wide':
      return CHART_LAYOUTS.WIDE_CENTERED;
    case 'tall':
      return CHART_LAYOUTS.TALL_CENTERED;
    case 'profile-left':
      return CHART_LAYOUTS.PROFILE_LEFT_CHART;
    case 'profile-right':
      return CHART_LAYOUTS.PROFILE_RIGHT_CHART;
    case 'full':
    default:
      return CHART_LAYOUTS.FULL_WIDTH;
  }
};

/**
 * Get standard format for a chart type
 * @param {string} chartType - Type of chart
 * @param {Object} overrides - Optional format overrides
 * @returns {Object} Format configuration
 */
export const getChartFormat = (chartType, overrides = {}) => {
  let baseFormat;

  switch (chartType.toLowerCase()) {
    case 'doughnut':
      baseFormat = CHART_FORMATS.DOUGHNUT;
      break;
    case 'pie':
      baseFormat = CHART_FORMATS.PIE;
      break;
    case 'bar':
    case 'bar-horizontal':
      baseFormat = CHART_FORMATS.BAR_HORIZONTAL;
      break;
    case 'bar-vertical':
    case 'column':
      baseFormat = CHART_FORMATS.BAR_VERTICAL;
      break;
    case 'bar-stacked':
    case 'stacked':
      baseFormat = CHART_FORMATS.BAR_STACKED;
      break;
    case 'line':
      baseFormat = CHART_FORMATS.LINE;
      break;
    default:
      baseFormat = {};
  }

  // Filter out undefined values from overrides to prevent them from overriding defaults
  const cleanOverrides = Object.entries(overrides).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

  return { ...baseFormat, ...cleanOverrides };
};

/**
 * Standard chart grid options
 */
export const CHART_GRID_OPTIONS = {
  chartArea: { fill: { color: 'FFFFFF' } },
  plotArea: { fill: { color: 'FFFFFF' } },
  catAxisLineColor: 'CCCCCC',
  valAxisLineColor: 'CCCCCC',
  valGridLine: {
    color: 'E0E0E0',
    width: 0.5,
    style: 'solid',
  },
  valAxisLabelFormatCode: '#,##0', // Thousands separator for value axis (e.g., 1,234,567)
};

/**
 * Standard font sizes
 */
export const CHART_FONT_SIZES = {
  title: 14,
  dataLabel: 9,
  axisLabel: 10,
  legend: 10,
};

/**
 * Placeholder text styling for no data scenarios
 */
export const PLACEHOLDER_STYLE = {
  x: 5,
  y: 3,
  w: 3,
  fontSize: 14,
  color: '999999',
  align: 'center',
  italic: true,
};

/**
 * Feed Card Layout Constants
 * Aligned with chart positioning for visual consistency
 */
export const FEED_LAYOUT = {
  // Card dimensions - matching half-width chart dimensions
  CARD_WIDTH: 6.0, // Matches LEFT_HALF and RIGHT_HALF chart widths
  CARD_HEIGHT: 2.65, // Increased to use available vertical space

  // Spacing between cards
  H_SPACING: 0.3, // Matches gap between LEFT_HALF and RIGHT_HALF (6.8 - 6.0 - 0.5 = 0.3)
  V_SPACING: 0.25, // Optimized vertical spacing

  // Starting position - aligned with chart layouts
  START_X: 0.5, // Matches standard chart x position (not 0.2)
  START_Y: CONTENT_START_Y, // Use the same Y position as charts for alignment

  // Content area within card
  CONTENT_PADDING_X: 0.1,
  CONTENT_PADDING_TOP: 0.7, // Space for avatar and profile info
  CONTENT_HEIGHT: 1.9, // Maximized for more text with larger cards

  // Footer positioning
  FOOTER_GAP: 0.15, // Small gap before slide footer

  // Cards per slide
  ITEMS_PER_SLIDE: 4,
  COLS: 2,
  ROWS: 2,
};

/**
 * Feed Text Size Thresholds
 * Dynamic sizing based on content length for optimal readability
 */
export const FEED_TEXT_SIZES = {
  BASE: 11, // Increased from 10 for better readability with larger cards
  THRESHOLDS: [
    { maxChars: 250, fontSize: 11 },
    { maxChars: 350, fontSize: 10 },
    { maxChars: 500, fontSize: 9 },
    { maxChars: 700, fontSize: 8.5 },
    { maxChars: 900, fontSize: 8 },
    { maxChars: Infinity, fontSize: 7.5 },
  ],
  LINE_ADJUSTMENTS: [
    { minLines: 9, maxFontSize: 8 },
    { minLines: 7, maxFontSize: 9 },
  ],
};

/**
 * Profile Card Layout Constants
 * Used for Top Profiles by Reach/Owned slides in Project Export
 * Follows same pattern as FEED_LAYOUT for consistency
 */
export const PROFILE_CARD_LAYOUT = {
  // Match FEED_LAYOUT dimensions for visual consistency
  CARD_WIDTH: 6.0, // Same as FEED_LAYOUT - fits 2 columns
  CARD_HEIGHT: 2.65, // Same as FEED_LAYOUT
  H_SPACING: 0.3, // Same as FEED_LAYOUT
  V_SPACING: 0.25, // Same as FEED_LAYOUT
  START_X: 0.5, // Same as FEED_LAYOUT
  START_Y: CONTENT_START_Y, // Use the same Y position as charts for alignment
  ITEMS_PER_SLIDE: 4, // 2x2 grid
  COLS: 2,
  ROWS: 2,
  // Profile-specific constants
  AVATAR_SIZE: 0.7, // Circular avatar size (inches)
  BIO_MAX_CHARS: 150, // Max bio characters (fits ~2-3 lines)
};
